import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, CreditCard, FileText, Zap, MapPin, CheckCircle2, Star } from 'lucide-react';
import { Countdown } from './Countdown';
import { CHECKOUT_LINKS } from '../config/checkout';
function TopBar() {
  return <div className="sticky top-0 z-50 bg-charcoal text-white py-2 px-4 shadow-md" data-id="element-82">
      <div className="max-w-5xl mx-auto flex items-center justify-between text-sm" data-id="element-83">
        <div className="flex items-center gap-2 font-bold tracking-wide" data-id="element-84">
          <span data-id="element-85">🇦🇷</span> AIRPACK™
        </div>
        <div className="flex items-center gap-3" data-id="element-86">
          <div className="hidden sm:flex items-center gap-2 text-gray-300" data-id="element-87">
            <span data-id="element-88">Termina en:</span>
            <Countdown compact data-id="element-89" />
          </div>
          <button onClick={() => document.getElementById('pricing')?.scrollIntoView({
          behavior: 'smooth'
        })} className="bg-ember hover:bg-ember-dark text-white px-3 py-1 rounded text-xs font-bold transition-colors" data-id="element-90">
            VER OFERTA
          </button>
        </div>
      </div>
    </div>;
}
function TestimonialStrip() {
  const testimonials = [{
    initials: 'MG',
    text: 'Organicé toda la semana en una tarde. Dejé de pedir delivery.',
    author: 'Marcela G.',
    city: 'Córdoba'
  }, {
    initials: 'LP',
    text: 'Las milanesas por fin me salen crocantes. Vale cada peso.',
    author: 'Laura P.',
    city: 'Rosario'
  }, {
    initials: 'RM',
    text: 'Hice un asado completo en el departamento. Mi señora no lo podía creer.',
    author: 'Roberto M.',
    city: 'Buenos Aires'
  }];
  return <div className="w-full bg-white border-y border-sand py-8 mt-16" data-id="element-91">
      <div className="max-w-6xl mx-auto px-4" data-id="element-92">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-93">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex flex-col gap-3 p-4 rounded-xl bg-cream/50 border border-sand/50" data-id="element-94">
              <div className="flex items-center gap-3" data-id="element-95">
                <div className="w-10 h-10 rounded-full bg-ember/10 text-ember flex items-center justify-center font-bold text-sm" data-id="element-96">
                  {t.initials}
                </div>
                <div className="flex text-amber-400" data-id="element-97">
                  {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-98" />)}
                </div>
              </div>
              <p className="text-charcoal/80 text-sm font-medium italic" data-id="element-99">
                "{t.text}"
              </p>
              <p className="text-xs text-charcoal/60 font-semibold" data-id="element-100">
                — {t.author}, {t.city}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>;
}
export function Hero() {
  return <>
      <TopBar data-id="element-101" />
      <section className="pt-16 md:pt-24 pb-0 relative overflow-hidden" data-id="element-102">
        {/* Subtle background texture/blob */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-ember/5 rounded-full blur-3xl -z-10" data-id="element-103" />

        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-104">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-sand text-xs font-bold tracking-widest uppercase text-charcoal/70 mb-8 shadow-sm" data-id="element-105">
            <span data-id="element-106">🇦🇷</span> AIRPACK™
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-5xl md:text-7xl font-serif font-bold text-charcoal leading-[1.1] tracking-tight mb-6" data-id="element-107">
            Todo tu año de cocina con Air Fryer.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-2xl md:text-3xl font-serif italic text-ember mb-6" data-id="element-108">
            Listo. Desde hoy.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-lg md:text-xl text-charcoal/80 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-109">
            El sistema N°1 de recetas de freidora de aire para el hogar
            argentino. Sin buscar. Sin improvisar. Sin pedir delivery de nuevo.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-110">
            {[{
            icon: FileText,
            text: '100% PDF'
          }, {
            icon: Zap,
            text: 'Acceso en 5 min'
          }, {
            icon: MapPin,
            text: 'Para freidoras argentinas'
          }, {
            icon: CheckCircle2,
            text: '1000 recetas reales'
          }].map((badge, i) => <div key={i} className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-md border border-sand text-xs font-semibold text-charcoal/80 shadow-sm" data-id="element-111">
                <badge.icon size={14} className="text-ember" data-id="element-112" />
                {badge.text}
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5
        }} className="flex flex-col items-center gap-6" data-id="element-113">
            <a href={CHECKOUT_LINKS.completo} target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center justify-center gap-3 bg-ember hover:bg-ember-dark text-white px-8 py-5 rounded-xl text-lg font-bold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl w-full sm:w-auto" data-id="element-114">
              <span className="text-2xl group-hover:animate-bounce" data-id="element-115">🔥</span>
              QUIERO MI AIRPACK AHORA
            </a>

            <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-xs font-medium text-charcoal/60" data-id="element-116">
              <span className="flex items-center gap-1" data-id="element-117">
                <ShieldCheck size={14} data-id="element-118" /> Garantía 7 días
              </span>
              <span className="flex items-center gap-1" data-id="element-119">
                <Lock size={14} data-id="element-120" /> Pago seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-121">
                <CreditCard size={14} data-id="element-122" /> Mercado Pago
              </span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.7
        }} className="mt-16 pt-8 border-t border-sand/60 flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm font-bold text-charcoal/80" data-id="element-123">
            <span data-id="element-124">+1.200 hogares</span>
            <span className="hidden sm:inline text-sand" data-id="element-125">•</span>
            <span data-id="element-126">6 Tomos</span>
            <span className="hidden sm:inline text-sand" data-id="element-127">•</span>
            <span data-id="element-128">4 Bonos</span>
            <span className="hidden sm:inline text-sand" data-id="element-129">•</span>
            <span className="flex items-center gap-1" data-id="element-130">
              4.9/5 <Star size={14} className="text-amber-400 fill-current" data-id="element-131" />
            </span>
          </motion.div>
        </div>

        <TestimonialStrip data-id="element-132" />
      </section>
    </>;
}